/*
This code was adapted from https://github.com/adaydesign/react-chakra-ui-table-v2

It was converted from TypeScript to JavaScript and enhanced to support pagination, sorting, and filtering.
*/

import React from "react"
import {
  Table as CKTable,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  HStack,
  Button,
  Select,
  Text,
  Input,
  Flex,
  TableContainer,
  Checkbox,
  MenuButton,
  MenuList,
  VStack,
  Box,
  Spinner,
  CheckboxGroup,
  Menu,
  Alert,
  AlertTitle,
  AlertIcon,
} from "@chakra-ui/react"

import {
  ArrowBackIcon,
  ArrowForwardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Search2Icon,
  SearchIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from "@chakra-ui/icons"
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getFilteredRowModel,
} from "@tanstack/react-table"
import { FaFile, FaTrash, FaDownload } from "react-icons/fa6"
import { SingleDatepicker } from "chakra-dayzed-datepicker"
import { Link } from "@chakra-ui/react"
import { getNumformat } from "@/lib/formatters"
import UploadRecords from "./UploadRecords"
import { Pagination, Limit, Offset, SchemaRecordCounts } from "../lib/store"

export const DEFAULT_PAGES = [25, 50, 100]

export const NoDataDisplay = () => {
  return (
    <Flex
      direction="column"
      p="2"
      gap="3"
      borderRadius="8"
      alignContent="left"
      bgColor="white"
      m="auto"
    >
      <Alert width="100%">
        <Flex direction={{ base: "column", md: "row" }} gap="4" width="100%">
          <Box
            width={{ base: "100%", md: "50%" }}
            position="relative"
            paddingBottom={{ base: "56.25%", md: "28.125%" }}
          >
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/RpZPYvfUwg4"
              title="Blind Insight Tutorial"
              style={{
                border: "none",
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
          <VStack
            justifyContent="space-between"
            alignItems="flex-start"
            width={{ base: "100%", md: "50%" }}
            spacing={2}
          >
            <VStack>
              <HStack width="100%" flexWrap="wrap">
                <AlertIcon flexShrink={0} />
                <AlertTitle
                  maxWidth="calc(100% - 40px)"
                  whiteSpace="normal"
                  overflowWrap="break-word"
                >
                  Looks like you need to add some data!
                </AlertTitle>
              </HStack>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Only encrypted data ever enters or exits Blind Insight, and we
                don&apos;t hold any keys. Download the Blind Proxy to encrypt
                data in your own environment before sending to Blind Insight.
                Soon, the Blind Proxy will be available as a browser extension,
                mobile library, and via secure enclave.
              </Text>
            </VStack>
            <Flex
              width="full"
              direction={{ base: "column", md: "row" }}
              justifyContent="right"
              gap={{ base: "1", md: "4" }}
              flexWrap="wrap"
            >
              <Link
                href="https://github.com/blind-insight/demo-datasets"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="secondary"
                  title="Download a sample dataset"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaFile />}
                >
                  Download a sample dataset
                </Button>
              </Link>
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </Flex>
          </VStack>
        </Flex>
      </Alert>
    </Flex>
  )
}

export const AllRecordsFilteredOutDisplay = () => {
  return (
    <Flex
      direction="column"
      p="2"
      gap="3"
      borderRadius="8"
      alignContent="left"
      bgColor="white"
      m="auto"
    >
      <Alert width="100%">
        <VStack>
          <HStack width="100%" flexWrap="wrap">
            <AlertIcon flexShrink={0} />
            <AlertTitle
              maxWidth="calc(100% - 40px)"
              whiteSpace="normal"
              overflowWrap="break-word"
            >
              No records match your filters.
            </AlertTitle>
          </HStack>
          <Text
            variant="base"
            width="100%"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            Try adjusting your filters to see more records.
          </Text>
        </VStack>
      </Alert>
    </Flex>
  )
}

export const HasProxyDataDisplay = ({
  uploadState,
  onUploadStateChange,
  onUpdateRecords,
  fileDropProps,
}) => {
  return (
    <Box w="100%" h="100%" overflowY="hidden">
      <Alert h="auto" flexGrow={1}>
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="flex-start"
          gap="8"
          minH="100%"
        >
          <VStack
            id="info-stack"
            justifyContent="space-between"
            flex="1"
            alignItems="stretch"
            spacing="4"
          >
            <Flex direction="column" gap="3">
              <HStack>
                <AlertIcon />
                <AlertTitle
                  maxWidth="calc(100% - 40px)"
                  whiteSpace="normal"
                  overflowWrap="break-word"
                >
                  Looks like you need to add some data!
                </AlertTitle>
              </HStack>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Congratulations! You are running the Blind Proxy. You are ready
                to start uploading. Your data must be in JSON or CSV format.{" "}
                <Link
                  href="https://docs.blindinsight.io/overview/what-is-blindinsight/#records"
                  isExternal
                  rel="noreferrer"
                  textDecoration="underline"
                  _hover={{ textDecoration: "none" }}
                >
                  Please see the documentation on how to format it.
                </Link>
              </Text>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Then when you&apos;re ready, go ahead and drag and drop your
                file into the box on the right and click <b>Upload</b>.
              </Text>
            </Flex>
            <Flex w="100%" justify="flex-end" mt="auto">
              <Link
                href="https://github.com/blind-insight/demo-datasets"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="secondary"
                  title="Download a sample dataset"
                  size={{ base: "sm", lg: "md" }}
                  rightIcon={<FaFile />}
                >
                  Download a sample dataset
                </Button>
              </Link>
            </Flex>
          </VStack>
          <Box
            flex="1"
            minW={{ base: "100%", md: "30em" }}
            maxW={{ base: "100%", md: "30em" }}
            position="relative"
          >
            <UploadRecords
              uploadState={uploadState}
              onUploadStateChange={onUploadStateChange}
              onUpdateRecords={onUpdateRecords}
              fileDropProps={fileDropProps}
            />
          </Box>
        </Flex>
      </Alert>
    </Box>
  )
}

export const LoadingDataDisplay = () => {
  return (
    <Flex
      direction="column"
      p={4}
      align="center"
      justify="center"
      bgColor="white"
    >
      <Spinner
        size="xl"
        boxSize="70px"
        thickness="0.25rem"
        mb={3}
        color="Gray.400"
      />
      <Text>Loading Data</Text>
    </Flex>
  )
}

export function DataTable({
  EncryptButton,
  hasProxy,
  data = [],
  columns,
  initialSortingState = [],
  initialColumnVisibility = {},
  filterIsOpen = false,
  rowCount = -1,
  uploadState,
  onUploadStateChange,
  onUpdateRecords,
  fileInputRef,
  selectedFile,
  onFileChange,
  onFileDrop,
  onOpenAttachment,
  onClearFile,
  onPageChange,
  schemaId,
}) {
  const [sorting, setSorting] = React.useState(initialSortingState)
  const [columnVisibility, setColumnVisibility] = React.useState(
    initialColumnVisibility,
  )
  const [limit, setLimit] = Limit.useState()
  const [offset, setOffset] = Offset.useState()
  const [, setPagination] = Pagination.useState()

  // 1. Refs to measure column widths
  const headerRefs = React.useRef({})
  const cellRefs = React.useRef({})

  // 2. Effect to auto-size columns to content
  React.useLayoutEffect(() => {
    if (!columns) return
    const newSizes = {}
    columns.forEach((col) => {
      const headerWidth = headerRefs.current[col.id]?.offsetWidth || 0
      let maxCellWidth = 0
      if (cellRefs.current[col.id]) {
        cellRefs.current[col.id].forEach((cell) => {
          if (cell && cell.offsetWidth > maxCellWidth) {
            maxCellWidth = cell.offsetWidth
          }
        })
      }
      // Add some padding
      const width = Math.max(headerWidth, maxCellWidth) + 24
      newSizes[col.id] = width
    })
    // Set column sizes in table state
    table.setColumnSizing(newSizes)
  }, [data, columns])

  const handlePageSizeChange = React.useCallback(
    (e) => {
      const newSize = Number(e.target.value)
      const currentStartRow = offset
      const newPageIndex = Math.floor(currentStartRow / newSize)
      const newOffset = newPageIndex * newSize

      setLimit(newSize)
      setOffset(newOffset)
      setPagination({
        pageIndex: newPageIndex,
        pageSize: newSize,
      })
    },
    [offset, setLimit, setOffset, setPagination],
  )

  const handlePaginationChange = React.useCallback(
    (newPagination) => {
      const newLimit = Number(newPagination.pageSize)
      const newPageIndex = Number(newPagination.pageIndex)
      const newOffset = newPageIndex * newLimit

      if (!isNaN(newLimit) && !isNaN(newOffset)) {
        setLimit(newLimit)
        setOffset(newOffset)
        setPagination({
          pageIndex: newPageIndex,
          pageSize: newLimit,
        })
        if (typeof onPageChange === "function") {
          onPageChange({
            pageIndex: newPageIndex,
            pageSize: newLimit,
            offset: newOffset,
            limit: newLimit,
          })
        }
      }
    },
    [setLimit, setOffset, setPagination, onPageChange],
  )

  const table = useReactTable({
    columns,
    data: data || [],
    initialState: {
      pagination: {
        pageIndex: Math.floor(offset / limit),
        pageSize: limit,
      },
    },
    autoResetPageIndex: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      width: "auto",
      minSize: 40,
      maxSize: 1000,
    },
    state: {
      pagination: {
        pageIndex: Math.floor(offset / limit),
        pageSize: limit,
      },
      sorting,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: handlePaginationChange,
    manualPagination: true,
    rowCount: rowCount,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  })

  const [jumpToPageValue, setJumpToPageValue] = React.useState(
    table ? table.getState().pagination.pageIndex + 1 : 1,
  )

  React.useEffect(() => {
    setJumpToPageValue(table.getState().pagination.pageIndex + 1)
  }, [table.getState().pagination.pageIndex])

  return (
    <VStack
      direction="column"
      flexGrow={1}
      minHeight={0}
      spacing="0"
      align="stretch"
    >
      {(data == null || data == undefined || data?.length == 0) &&
      SchemaRecordCounts.getValue()[schemaId] !== undefined &&
      SchemaRecordCounts.getValue()[schemaId] > 0 ? (
        <Box width="100%" maxWidth="100%" overflow="hidden" flex="1">
          <AllRecordsFilteredOutDisplay />
        </Box>
      ) : hasProxy && data?.length === 0 ? (
        <Box width="100%" maxWidth="100%" overflow="auto" flex="1" mt="1em">
          <HasProxyDataDisplay
            uploadState={uploadState}
            onUploadStateChange={onUploadStateChange}
            onUpdateRecords={onUpdateRecords}
            fileDropProps={{
              fileInputRef,
              selectedFile,
              onFileChange,
              onFileDrop,
              onOpenAttachment,
              onClearFile,
            }}
          />
        </Box>
      ) : (data == null || data == undefined || data?.length == 0) &&
        (SchemaRecordCounts.getValue()[schemaId] === undefined ||
          SchemaRecordCounts.getValue()[schemaId] === 0) ? (
        <Box width="100%" maxWidth="100%" overflow="hidden" flex="1">
          <NoDataDisplay />
        </Box>
      ) : (
        <>
          {/* table controls  */}
          <HStack
            mt="2"
            align="center"
            justify="space-between"
            bg="white"
            p="0.25em"
            borderRadius="0.5em 0.5em 0 0"
            w="full"
            wrap="wrap"
            gap="3"
            flexShrink={0}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <HStack flex="1" minW="0">
              <Text minW="fit-content" ml="1em">
                Go To :{" "}
              </Text>
              <Input
                minW="3em"
                maxW="5em"
                height="2em"
                type="number"
                borderRadius="0.25em"
                defaultValue={table.getState().pagination.pageIndex + 1}
                value={
                  jumpToPageValue ?? table.getState().pagination.pageIndex + 1
                }
                onChange={(e) => {
                  setJumpToPageValue(e.target.value)
                }}
                onBlur={() => {
                  let page = Number(jumpToPageValue) - 1
                  if (isNaN(page)) page = 0
                  if (page < 0) page = 0
                  if (page > table.getPageCount() - 1)
                    page = table.getPageCount() - 1
                  handlePaginationChange({
                    pageIndex: page,
                    pageSize: table.getState().pagination.pageSize,
                  })
                  setJumpToPageValue(page + 1)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    let page = Number(jumpToPageValue) - 1
                    if (isNaN(page)) page = 0
                    if (page < 0) page = 0
                    if (page > table.getPageCount() - 1)
                      page = table.getPageCount() - 1
                    handlePaginationChange({
                      pageIndex: page,
                      pageSize: table.getState().pagination.pageSize,
                    })
                    setJumpToPageValue(page + 1)
                  }
                }}
                size="sm"
              />
              <Select
                height="2em"
                borderRadius="0.25em"
                maxW="6em"
                minW="fit-content"
                fontSize="1em"
                value={table.getState().pagination.pageSize}
                onChange={handlePageSizeChange}
              >
                {DEFAULT_PAGES.map((pageSize, index) => (
                  <option key={`page-${index}`} value={pageSize}>
                    <Text fontSize="1em">Show {pageSize} rows</Text>
                  </option>
                ))}
              </Select>
            </HStack>

            <HStack flex="1" justify="center" minW="0">
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: 0,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!table.getCanPreviousPage()}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: table.getState().pagination.pageIndex - 1,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!table.getCanPreviousPage()}
              >
                <ChevronLeftIcon />
              </Button>
              <Text minW="fit-content">
                {`Page ${
                  table.getState().pagination.pageIndex + 1
                } / ${table.getPageCount()}`}
              </Text>
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: table.getState().pagination.pageIndex + 1,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!table.getCanNextPage()}
              >
                <ChevronRightIcon />
              </Button>
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: table.getPageCount() - 1,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!table.getCanNextPage()}
              >
                <ArrowForwardIcon />
              </Button>
              <Text color="Gray.500" ml={4} whiteSpace="nowrap">
                Showing{" "}
                {table.getState().pagination.pageIndex === 0
                  ? table.getState().pagination.pageIndex + 1
                  : table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                    1}
                -
                {(table.getState().pagination.pageIndex + 1) *
                  table.getState().pagination.pageSize <
                table.getRowCount()
                  ? (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize
                  : table.getRowCount()}{" "}
                of {getNumformat(table.getRowCount())}
              </Text>
            </HStack>

            <Box
              flex="1"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <EncryptButton />
            </Box>
          </HStack>
          {/* table header row */}
          <TableContainer
            flex="1"
            minHeight={0}
            height="100%"
            overflowY="auto"
            overflowX="auto"
            bg="white"
            width="100%"
            maxW="100%"
            borderRadius="0em 0em 0.5em 0.5em"
            boxShadow="lg"
          >
            <CKTable
              size="md"
              width="100%"
              variant="dataTable"
              sx={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "100%",
              }}
            >
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => {
                  return (
                    <Tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const meta = header.column.columnDef
                        return (
                          <Th
                            fontSize="1em"
                            key={header.id}
                            isNumeric={meta?.isNumeric}
                            colSpan={header.colSpan}
                            sx={{
                              position: "relative",
                              width: header.getSize()
                                ? `${header.getSize()}px`
                                : "auto",
                              maxWidth: "500px",
                              minWidth: "40px",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              textTransform: "none",
                            }}
                            ref={(el) => {
                              if (el) headerRefs.current[header.column.id] = el
                            }}
                          >
                            <HStack
                              minH="unset"
                              alignItems="center"
                              onClick={header.column.getToggleSortingHandler()}
                              cursor="pointer"
                              gap="0.5rem"
                              w="full"
                              sx={{
                                paddingY: "0 !important",
                              }}
                            >
                              <Text
                                isTruncated
                                width="100%"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                fontSize="1em"
                                lineHeight="1"
                                paddingY="0"
                                height="auto"
                                alignItems="center"
                                display="block"
                                color="#4A5568"
                                title={
                                  header.isPlaceholder
                                    ? ""
                                    : String(
                                        flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        ),
                                      )
                                }
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                              </Text>
                              {header.column.getCanResize() && (
                                <Box
                                  onDoubleClick={() =>
                                    header.column.resetSize()
                                  }
                                  onMouseDown={header.getResizeHandler()}
                                  onTouchStart={header.getResizeHandler()}
                                  className={`resizer ${
                                    header.column.getIsResizing()
                                      ? "isResizing"
                                      : ""
                                  }`}
                                  sx={{
                                    position: "absolute",
                                    right: 0,
                                    top: 0,
                                    height: "100%",
                                    width: "6px",
                                    cursor: "col-resize",
                                    zIndex: 1,
                                    userSelect: "none",
                                    background: header.column.getIsResizing()
                                      ? "#3182ce"
                                      : "transparent",
                                    transition: "background 0.2s",
                                  }}
                                />
                              )}
                              <Box
                                w="1rem"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                                paddingY="0"
                              >
                                {header.column.getIsSorted() &&
                                  (header.column.getIsSorted() === "desc" ? (
                                    <TriangleDownIcon aria-label="sorted descending" />
                                  ) : (
                                    <TriangleUpIcon aria-label="sorted ascending" />
                                  ))}
                              </Box>
                            </HStack>
                            <Box>
                              {filterIsOpen && (
                                <Menu closeOnSelect={false}>
                                  <MenuButton
                                    icon={
                                      header.column.getFilterValue() ===
                                      undefined ? (
                                        <SearchIcon />
                                      ) : (
                                        <Search2Icon />
                                      )
                                    }
                                    isRound={true}
                                    variant="ghost"
                                    colorScheme={
                                      header.column.getFilterValue() ===
                                      undefined
                                        ? "gray"
                                        : "orange"
                                    }
                                    fontSize="1rem"
                                    aria-label="column filter"
                                    size="sm"
                                  />
                                  <MenuList p="1">
                                    <Flex w="full" direction="column" gap="1">
                                      {header.column.getCanFilter() && (
                                        <Flex>
                                          <Filter
                                            column={header.column}
                                            table={table}
                                          />
                                        </Flex>
                                      )}
                                      <Button
                                        rightIcon={<FaTrash />}
                                        colorScheme="blue"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          header.column.setFilterValue(
                                            undefined,
                                          )
                                        }
                                      >
                                        Reset
                                      </Button>
                                    </Flex>
                                  </MenuList>
                                </Menu>
                              )}
                            </Box>
                          </Th>
                        )
                      })}
                    </Tr>
                  )
                })}
              </Thead>
              <Tbody>
                {table.getRowModel().rows?.map((row) => (
                  <Tr
                    key={row.id}
                    _hover={{ shadow: "md", bg: "blackAlpha.50" }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Td
                          key={cell.id}
                          sx={{
                            width: cell.column.getSize()
                              ? `${cell.column.getSize()}px`
                              : "auto",
                            maxWidth: "500px",
                            minWidth: "40px",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                          ref={(el) => {
                            if (!cellRefs.current[cell.column.id]) {
                              cellRefs.current[cell.column.id] = []
                            }
                            if (
                              el &&
                              !cellRefs.current[cell.column.id].includes(el)
                            ) {
                              cellRefs.current[cell.column.id].push(el)
                            }
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Td>
                      )
                    })}
                  </Tr>
                ))}
              </Tbody>
            </CKTable>
          </TableContainer>
        </>
      )}
    </VStack>
  )
}

// Filter Component
const Filter = ({ column, table }) => {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id)

  const columnFilterValue = column.getFilterValue()

  const columnType = getType(firstValue)
  const facets = column?.getFacetedUniqueValues()
  // Custom facets if keys is array then sum the equal value and remove duplicate
  const customFacets = new Map()
  for (const [key, value] of facets) {
    if (Array.isArray(key)) {
      for (const k of key) {
        const prevValue = customFacets.get(k) || 0
        customFacets.set(k, prevValue + value)
      }
    } else {
      const prevValue = customFacets.get(key) || 0
      customFacets.set(key, prevValue + value)
    }
  }

  const sortedUniqueValues = React.useMemo(
    () =>
      columnType === "number" || columnType === "date"
        ? []
        : Array.from(customFacets.keys()).sort(),
    [customFacets],
  )

  const meta = column.columnDef.meta
  const switchEval =
    meta && meta.columnType
      ? meta.columnType.toLowerCase()
      : getType(firstValue)

  switch (switchEval) {
    case "number":
      return (
        <HStack w="full" spacing={1}>
          <Input
            type="number"
            size="sm"
            min={Number(
              column.getFacetedMinMaxValues()?.[0] !== undefined &&
                column.getFacetedMinMaxValues()?.[0] !== null
                ? column.getFacetedMinMaxValues()?.[0]
                : "",
            )}
            max={Number(
              column.getFacetedMinMaxValues()?.[1] !== undefined &&
                column.getFacetedMinMaxValues()?.[1] !== null
                ? column.getFacetedMinMaxValues()?.[1]
                : "",
            )}
            value={columnFilterValue?.[0] ?? ""}
            onChange={(e) =>
              column.setFilterValue((old) => [e.target.value, old?.[1]])
            }
            placeholder={`min ${
              column.getFacetedMinMaxValues()?.[0] !== undefined &&
              column.getFacetedMinMaxValues()?.[0] !== null
                ? `(${column.getFacetedMinMaxValues()?.[0]})`
                : ""
            }`}
          />
          <Input
            type="number"
            size="sm"
            min={Number(
              column.getFacetedMinMaxValues()?.[0] !== undefined &&
                column.getFacetedMinMaxValues()?.[0] !== null
                ? column.getFacetedMinMaxValues()?.[0]
                : "",
            )}
            max={Number(
              column.getFacetedMinMaxValues()?.[1] !== undefined &&
                column.getFacetedMinMaxValues()?.[1] !== null
                ? column.getFacetedMinMaxValues()?.[1]
                : "",
            )}
            value={columnFilterValue?.[1] ?? ""}
            onChange={(e) => {
              column.setFilterValue((old) => [old?.[0], e.target.value])
            }}
            placeholder={`max ${
              column.getFacetedMinMaxValues()?.[1] !== undefined &&
              column.getFacetedMinMaxValues()?.[1] !== null
                ? `(${column.getFacetedMinMaxValues()?.[1]})`
                : ""
            }`}
          />
        </HStack>
      )

    case "date":
      return (
        <HStack w="full" spacing={1}>
          <SingleDatepicker
            name="start-date-input"
            date={columnFilterValue?.[0] ?? undefined}
            onDateChange={(newStartDate) =>
              column.setFilterValue((old) => [newStartDate, old?.[1]])
            }
            propsConfigs={{
              inputProps: {
                size: "sm",
                placeholder: "from...",
              },
            }}
            configs={{
              dateFormat: "yyyy-MM-dd",
            }}
          />

          <SingleDatepicker
            name="end-date-input"
            date={columnFilterValue?.[1] ?? undefined}
            onDateChange={(newEndDate) =>
              column.setFilterValue((old) => [old?.[0], newEndDate])
            }
            propsConfigs={{
              inputProps: {
                size: "sm",
                placeholder: "to...",
              },
            }}
            configs={{
              dateFormat: "yyyy-MM-dd",
            }}
          />
        </HStack>
      )

    case "boolean":
      return (
        <HStack w="full" spacing={1}>
          <Select
            id={column.id + "list"}
            placeholder={`select... (${customFacets.size})`}
            size="sm"
            onChange={(e) => {
              column.setFilterValue(
                !e.target.value ? undefined : e.target.value === "true",
              )
            }}
            value={column.getFilterValue() ?? ""}
          >
            {sortedUniqueValues.slice(0, 5000).map((value) => (
              <option value={value} key={value}>
                {value ? "True" : "False"}
              </option>
            ))}
          </Select>
        </HStack>
      )

    case "enum":
      return (
        <Flex w="full">
          <Select
            id={column.id + "list"}
            placeholder={`select... (${customFacets.size})`}
            size="sm"
            onChange={(e) => column.setFilterValue(e.target.value)}
            value={column.getFilterValue() || ""}
          >
            {sortedUniqueValues.slice(0, 5000).map((value) => (
              <option value={value} key={value}>
                {value}
              </option>
            ))}
          </Select>
        </Flex>
      )

    case "multienum":
      return (
        <Flex w="full">
          <CheckboxGroup
            defaultValue={sortedUniqueValues.slice(0, 5000)}
            onChange={(e) => column.setFilterValue(e)}
            value={column.getFilterValue() || sortedUniqueValues.slice(0, 5000)}
          >
            <VStack align="start">
              {sortedUniqueValues.slice(0, 5000).map((value) => (
                <Checkbox value={value} key={value}>
                  {value}
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        </Flex>
      )

    case "array":
      return (
        <Flex w="full">
          <CheckboxGroup
            defaultValue={sortedUniqueValues.slice(0, 5000)}
            onChange={(e) => column.setFilterValue(e)}
            value={column.getFilterValue() || sortedUniqueValues.slice(0, 5000)}
          >
            <VStack align="start">
              {sortedUniqueValues.slice(0, 5000).map((value) => (
                <Checkbox value={value} key={value}>
                  {value}
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        </Flex>
      )

    case "string":
      return (
        <Flex w="full">
          <Input
            type="text"
            size="sm"
            value={columnFilterValue ?? ""}
            onChange={(e) => column.setFilterValue(e.target.value)}
            placeholder={`find...`}
            list={column.id + "list"}
          />
        </Flex>
      )

    default:
      return (
        <Flex w="full">
          <Input
            type="text"
            size="sm"
            value={columnFilterValue ?? ""}
            onChange={(e) => column.setFilterValue(e.target.value)}
            placeholder={`find...`}
            list={column.id + "list"}
          />
        </Flex>
      )
  }
}

function getType(variable) {
  if (variable === null) {
    return "null"
  }

  switch (typeof variable) {
    case "string":
      return "string"
    case "number":
      return "number"
    case "boolean":
      return "boolean"
    case "undefined":
      return "undefined"
    case "function":
      return "function"
    case "object":
      if (variable instanceof Date) {
        return "date"
      }
      if (Array.isArray(variable)) {
        return "array"
      }
      return "object"
    default:
      return "unknown"
  }
}

// summary column
export const getSummary = (table, field) => {
  const sum = table
    .getFilteredRowModel()
    .rows.reduce((total, row) => total + row.getValue(field), 0)
  return sum || 0
}
